import { FontStyleHelper } from './font-style.helper';
import {
    FONT_SIZE_WHITE_LIST, LINE_HEIGHT_DISPLAY_TO_ENUM_VALUE_MAP, NON_INHERITED_QUILL_CSS_PROPERTIES,
    PASTED_INHERITED_ATTRIBUTES, QUILL_CSS_PROPERTIES
} from '../quill.const';
import { MSOListHelper } from './mso-list.helper';

const IS_NUMERIC_REGEX = /^-?\d+\.?\d*(em)?$/;
const IS_PERCENTAGE_REGEX = /\d+(?:\.\d+)?%/;

/**
 * @dynamic resolves static lambda issue with package build
 * https://github.com/ng-packagr/ng-packagr/issues/696
 */

// @dynamic

/**
 * Contains functions to translate pasted text to useable html.  Translates styles
 * to values that will work in quill.js
 */
export class PastedTextFormatter {
    public static readonly BOLD_TAG_NAME = 'B';

    /**
     * Applies translation of styles to provided node, and walks children translating styles.
     */
    public static formatAndWalkNodeTree = (node: HTMLElement): HTMLElement => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return node;
        }

        const translatedNode = PastedTextFormatter.translateStyles(<HTMLElement>node);
        const childNodes = Array.from(node.childNodes);

        childNodes.forEach(childNode => {
            if (childNode.nodeType !== Node.ELEMENT_NODE) {
                translatedNode.appendChild(childNode);
                return;
            }

            const translatedChildNode: HTMLElement = PastedTextFormatter.formatAndWalkNodeTree(<HTMLElement>childNode);
            const appendedNode: HTMLElement = translatedChildNode || <HTMLElement>childNode;
            PastedTextFormatter.passLineHeightToParentNode(translatedNode, appendedNode);
            translatedNode.append(appendedNode);
        });

        return translatedNode;
    };

    /**
     * Returns node with styles translated to approved quill values
     */
    public static translateStyles = (node: HTMLElement): HTMLElement => {
        const clone = PastedTextFormatter.cloneNode(node);

        let formattedNode = clone;
        formattedNode = PastedTextFormatter.formatFontFamily(formattedNode);
        formattedNode = PastedTextFormatter.formatFontSize(formattedNode);
        formattedNode = PastedTextFormatter.formatLineHeight(formattedNode);
        formattedNode = PastedTextFormatter.formatBoldTags(formattedNode);
        formattedNode = PastedTextFormatter.formatParagraphIndents(formattedNode);

        return formattedNode;
    };

    /**
     * Converts bold tags that specify a font weight to a paragraph tag
     */
    public static formatBoldTags = (htmlElement: HTMLElement) => {
        if (htmlElement.tagName === PastedTextFormatter.BOLD_TAG_NAME) {
            const fontWeight = htmlElement.style.fontWeight;
            if (fontWeight) {
                const paragraphElement = PastedTextFormatter.convertHTMLElementToParagraph(htmlElement);

                return paragraphElement;
            }
        }

        return htmlElement;
    };

    /**
     * Converts HTML element to a a paragraph element
     */
    public static convertHTMLElementToParagraph = (node): HTMLElement => {
        const clone: any = document.createElement('P');
        const classList = Array.from(node.classList);

        classList.forEach(className => {
            clone.classList.add(className);
        });

        QUILL_CSS_PROPERTIES.forEach(property => {
            clone.style[property] = node.style[property];
        });

        return clone;
    };

    public static cloneNode = (node): HTMLElement => {
        const clone = document.createElement(node.tagName);

        PASTED_INHERITED_ATTRIBUTES.forEach(attribute => {
            if (node[attribute]) {
                clone[attribute] = node[attribute];
            }
        });

        QUILL_CSS_PROPERTIES.forEach(property => {
            clone.style[property] = node.style[property];
            // get inherited properties
            if (!NON_INHERITED_QUILL_CSS_PROPERTIES.includes(property) && !node.style[property] && node.parentNode?.style[property]) {
                clone.style[property] = node.parentNode.style[property];
            }
        });

        return clone;
    };

    /**
     * Changes the font family of a node to be one of the white listed values for quill
     */
    public static formatFontFamily = (node: HTMLElement): HTMLElement => {
        const fontFamily = node.style.fontFamily;

        if (!fontFamily) {
            return node;
        }

        const translatedFontFamily = PastedTextFormatter.translateFontFamily(fontFamily);
        if (!translatedFontFamily) {
            return node;
        }

        node.style.fontFamily = translatedFontFamily;

        return node;
    };

    public static translateFontFamily = (fontFamily: string) => {
        if (!fontFamily) {
            return;
        }

        return FontStyleHelper.getMatchingQuillFont(fontFamily);
    };

    // convert pt unit to px and match whitelist font size
    public static formatFontSize(node: HTMLElement): HTMLElement {
        const fontSize: string = node.style.fontSize;

        if (!fontSize) {
            return node;
        }

        let num = parseInt(fontSize, 10);
        if (fontSize.endsWith('pt')) {
            num = num / 0.75;
        }
        if (!isNaN(num)) {
            num = this.findClosestValue(num, FONT_SIZE_WHITE_LIST);
            node.style.fontSize = num + 'px';
        }

        return node;
    }

    public static formatLineHeight = (node: HTMLElement) => {
        let lineHeight: string = node.style.lineHeight;
        let isLineHeightNumeric: boolean = IS_NUMERIC_REGEX.test(lineHeight);
        const isLineHeightPercentage: boolean = IS_PERCENTAGE_REGEX.test(lineHeight);

        if (!lineHeight) {
            return node;
        }

        const lineHeightNumericValue = isLineHeightPercentage ? null : this.getNumericPxValueFromStyleValue(lineHeight);

        if (lineHeightNumericValue && node.style.fontSize) {
            lineHeight = lineHeightNumericValue / parseInt(node.style.fontSize, 10) + '';
            isLineHeightNumeric = true;
        }

        if (isLineHeightNumeric) {
            node.style.lineHeight = PastedTextFormatter.findClosestWhitelistLineHeight(+lineHeight) * 100 + '%';

            return node;
        }

        if (isLineHeightPercentage) {
            const lineHeightNumericPercentage: number = +lineHeight.substring(0, lineHeight.length - 1);
            const lineHeightAsFactorOf100 = lineHeightNumericPercentage / 100;

            node.style.lineHeight = PastedTextFormatter.findClosestWhitelistLineHeight(lineHeightAsFactorOf100) * 100 + '%';

            return node;
        }

        return node;
    };

    public static findClosestWhitelistLineHeight = (lineHeight: number) => {
        const lineHeightDifferenceToDisplayValueMap: Map<number, number> = new Map();

        for (const entry of LINE_HEIGHT_DISPLAY_TO_ENUM_VALUE_MAP.entries()) {
            const displayValue = entry[0];
            const enumValue = entry[1];

            const difference = Math.abs(lineHeight - enumValue);
            lineHeightDifferenceToDisplayValueMap.set(difference, displayValue);
        }

        const sortedDifferenceKeys = Array.from(lineHeightDifferenceToDisplayValueMap.keys()).sort((a, b) => {
            if (a < b) {
                return -1;
            }

            if (a > b) {
                return 1;
            }

            if (a === b) {
                return 0;
            }
        });

        const closestDisplayValue = lineHeightDifferenceToDisplayValueMap.get(sortedDifferenceKeys[0]);
        const closestEnumValue = LINE_HEIGHT_DISPLAY_TO_ENUM_VALUE_MAP.get(closestDisplayValue);

        return closestEnumValue;
    };

    /**
     * Word web sets line height on span, while we specify line height on block level element, so in this case we need to set parent line height
     */
    private static passLineHeightToParentNode(parentNode: HTMLElement, childNode: HTMLElement) {
        if (childNode?.style?.lineHeight && !parentNode.style?.lineHeight && childNode.tagName === 'SPAN' && parentNode.tagName === 'P') {
            parentNode.style.lineHeight = childNode.style.lineHeight;
        }
    }

    /**
     * Formats indents on paragraphs
     */
    public static formatParagraphIndents(element: HTMLElement): HTMLElement {
        if (MSOListHelper.isMSOListItem(element)) {
            return element;
        }

        const marginLeftPx = this.getNumericPxValueFromStyleValue(element.style.marginLeft);
        let blockIndentLevel = Math.round(marginLeftPx / 48);

        if (blockIndentLevel > 8) {
            blockIndentLevel = 8;
        }

        element.style.removeProperty('margin-left');

        const textIndentPx = this.getNumericPxValueFromStyleValue(element.style.textIndent);
        // Quill block indent uses 3em, which is 42px. Since Quill converts any number of text-indent to a \t, we cannot rely on text indent style
        // for paragraph indentation, so to mimic 42px, it is roughly 10 white spaces when the font size 14px, and inconsistency between 10 white
        // spaces and 42px should be expected
        const spacesPerIndent = 10, pxsPerIndent = 48;
        const paragraphIndentLevel = Math.round(textIndentPx / pxsPerIndent);
        if (paragraphIndentLevel > 0) {
            const indentTextNode = document.createTextNode('\xa0'.repeat(spacesPerIndent * paragraphIndentLevel));
            element.appendChild(indentTextNode);
            element.style.removeProperty('text-indent');
        }

        return element;
    }

    /**
     * Returns a numeric value without a unit of measurement
     */
    public static getNumericPxValueFromStyleValue(value: string): number {
        let numericValue = +value.replace(/[^0-9.]/g, '');
        if (value.endsWith('pt')) {
            numericValue = numericValue / 0.75;
        } else if (value.endsWith('in')) {
            numericValue = numericValue * 48;
        }
        if (isNaN(numericValue)) {
            return 0;
        }

        return numericValue;
    }

    /**
     * Returns the calculated css value of the rendered element
     */
    public static getRenderedCssValue = (element: HTMLElement, property) => {
        if (element.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        return window.getComputedStyle(element, null).getPropertyValue(property);
    };

    /**
     * utility function to find the closest value from an number array
     * If the array length is undetermined and potentially long but sorted, consider using a binary search
     * returns the closest value itself not the index
     */
    public static findClosestValue(target: number, array: number[]): number {
        return array.reduce(function(prev, curr) {
            return (Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev);
        });
    }
}
